import "./styles.css"

import { AppDispatch, RootState } from "src/store/store";
import { Box, Button, HStack, IconButton, useMediaQuery } from "@chakra-ui/react";
import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppRoutes } from "src/common/constants/constantEnums";
import { EnglishLabels } from "src/common/constants/englishLabels";
import { GlobalLangSelector } from "src/common/globalLangSelector";
import { HindiLabels } from "src/common/constants/hindiLabels";
import MksLogo from "src/resources/icons/mksLogo";
import SideDrawer from "./sideDrawer";
import { addBreadcrumb } from "src/store/features/breadCrumb";
import { GlobalUtils } from "src/utils/globalUtils";

export interface NavMenu {
    name: string;
    hindi: string;
    onClick: () => void;
}

interface NavbarProps {
    scrollToAbout: () => void;
    scrollToEvents: () => void;
    scrollToSanstha: () => void;
    scrollToContact: () => void;
    isScrolled: boolean;
    activeSection: string;
}


const HomeNavbar: React.FC<NavbarProps> = (props: NavbarProps) => {
    const { scrollToAbout, scrollToEvents, scrollToContact, isScrolled, activeSection } = props;
    const [sideDrawerOpen, setSideDrawerOpen] = useState(false);
    const location = useLocation();
    const [isMobile] = useMediaQuery("(max-width: 426px)");
    const [isTablet] = useMediaQuery("(min-width: 426px) and (max-width: 768px)");
    const [isSmallScreen] = useMediaQuery("(min-width: 768px) and (max-width: 1000px)");
    const globalInfo = useSelector((state: RootState) => state.globalInfo);
    const { globalLanguage } = globalInfo;
    const isHindiLabels = globalLanguage === "HI";
    const isDonationPage = location.pathname.includes("donation");
    const navigate = useNavigate();
    const [userRole, setUserRole] = useState<string | null>(null);

    useEffect(() => {
        const updateRoleFromToken = () => {
            const token = window.localStorage.getItem("authToken") || "";
            if (!token) {
                setUserRole(null);
                return;
            }
            const { role, approved } = GlobalUtils.getAuthStateFromToken(token);
            if (!approved) {
                setUserRole(null);
                return;
            }
            setUserRole(role || null);
        };

        updateRoleFromToken();

        const onStorage = (e: StorageEvent) => {
            if (["authToken"].includes(e.key || "")) updateRoleFromToken();
        };

        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [location.pathname]);

    const dispatch = useDispatch<AppDispatch>();
    
    const handleNavigation = (label: string): void => {
        const navigationMap: Record<string, () => void> = {
            [EnglishLabels.Matrimony]: () => {
                // Open Matrimony in a new tab (preserves SPA route)
                const url = `${window.location.origin}${AppRoutes.MATRIMONY}`;
                window.open(url, "_blank", "noopener,noreferrer");
            },
            [EnglishLabels.Join_us]: () => navigate(AppRoutes.JOIN_US),
            [EnglishLabels.About_us]: scrollToAbout,
            [EnglishLabels.Events]: scrollToEvents,
            [EnglishLabels.Contact_us]: scrollToContact,
            [EnglishLabels.Donation]: handleNavDonateClick
        };
    
        if (location.pathname !== AppRoutes.LANDING_PAGE) {
            if (label === EnglishLabels.About_us || label === EnglishLabels.Events || label === EnglishLabels.Contact_us) {
                navigate(AppRoutes.LANDING_PAGE);
                window.sessionStorage.setItem("scrollTo", label)
            }
            navigationMap[label]();
        } else {
         if (label in navigationMap) {
                navigationMap[label]();
            }
        }
    };    

    const navButtons: NavMenu[] = [
        {
            name: EnglishLabels.About_us,
            hindi: HindiLabels.About_us,
            onClick: ()=> handleNavigation(EnglishLabels.About_us)
        },
        {
            name: EnglishLabels.Events,
            hindi: HindiLabels.Events,
            onClick: ()=> handleNavigation(EnglishLabels.Events)
        },
        // {
        //     name: "Sanstha",
        //     onClick: scrollToSanstha
        // },
        {
            name: EnglishLabels.Contact_us,
            hindi: HindiLabels.Contact_us,
            onClick: ()=> handleNavigation(EnglishLabels.Contact_us)
        },
        {
            name: EnglishLabels.Matrimony,
            hindi: HindiLabels.Matrimony,
            onClick: ()=> handleNavigation(EnglishLabels.Matrimony)
        },
        // Commented out Donation from navbar
        // {
        //     name: EnglishLabels.Donation,
        //     hindi: HindiLabels.Donation,
        //     onClick: ()=> handleNavigation(EnglishLabels.Donation)
        // },
        {
            name:
                userRole === "ADMIN" || userRole === "SUPER_ADMIN"
                    ? "Admin"
                    : userRole === "USER"
                    ? "Profile"
                    : userRole === "CONSULTANT"
                    ? "Profile"
                    : EnglishLabels.Join_us,
            hindi:
                userRole === "ADMIN" || userRole === "SUPER_ADMIN"
                    ? "व्यवस्थापक"
                    : userRole === "USER"
                    ? "प्रोफ़ाइल"
                    : userRole === "CONSULTANT"
                    ? "प्रोफ़ाइल"
                    : HindiLabels.Join_us,
            onClick: () => {
                if (userRole === "ADMIN" || userRole === "SUPER_ADMIN") navigate(AppRoutes.ADMIN, { state: { fromApp: true } });
                else if (userRole === "USER") navigate(AppRoutes.PROFILE, { state: { fromApp: true } });
                else if (userRole === "CONSULTANT") navigate(AppRoutes.PROFILE, { state: { fromApp: true } });
                else handleNavigation(EnglishLabels.Join_us);
            },
        },
    ];

    const handleNavDonateClick = (): void => {
        if (!isDonationPage) {
            dispatch(addBreadcrumb({ label: "Donation", path: AppRoutes.DONATION_PAGE }));
            navigate(AppRoutes.DONATION_PAGE);
        }
    }

    const handleNavClick = (nav: { name: string, onClick: () => void }): void => {
        nav.onClick();
        if (sideDrawerOpen) {
            setSideDrawerOpen(false);
        }
    }

    const renderLangSelectorMenuRenderer = (): ReactElement => {
        return (
            <>
                <Box hidden={!(isMobile || isTablet || isSmallScreen)}>
                    <GlobalLangSelector />
                </Box>

                <HStack
                    px={2}
                    alignItems="center"
                    height="4rem"
                    bg="white"
                    borderRadius="10px"
                    boxShadow={(isMobile || isTablet || isSmallScreen || isScrolled) ? "" : "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)"}
                >
                    {isMobile || isTablet || isSmallScreen ?
                        <IconButton bgColor="transparent" aria-label="HamburgerIcon">
                            {sideDrawerOpen ?
                                <CloseIcon
                                    boxSize={5}
                                    onClick={() => setSideDrawerOpen(false)}
                                />
                                :
                                <HamburgerIcon
                                    boxSize={8}
                                    boxShadow="none"
                                    onClick={() => setSideDrawerOpen(true)}
                                />
                            }
                        </IconButton>
                        :
                        <GlobalLangSelector />
                    }
                </HStack>
            </>
        )
    }
    
    return (
        <HStack
            py={2}
            px={[0, 0, 0, 10]}
            transition="bg 0.3s ease"
            bg={(isMobile || isTablet || isSmallScreen || isScrolled) ? "white" : "transparent"}
            justifyContent="space-evenly"
            position="absolute"
            w="100%"
            boxShadow={(isMobile || isTablet || isSmallScreen || isScrolled) ? "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)" : ""}
        >
            <Box
                borderRadius="10px"
                bg="white"
                height="4rem"
                width="4rem"
                boxShadow={(isMobile || isTablet || isSmallScreen || isScrolled) ? "" : "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)"}
            >
                <Link to={AppRoutes.LANDING_PAGE}><MksLogo /></Link>
            </Box>
            <HStack
                hidden={isMobile || isTablet || isSmallScreen}
                w="55%"
                px={2}
                height="4rem"
                bg="white"
                borderRadius="10px"
                justifyContent="space-around"
                paddingX={10}
                boxShadow={(isMobile || isTablet || isSmallScreen || isScrolled) ? "" : "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 8px 16px 0px rgba(0, 0, 0, 0.08)"}
            >
                    {navButtons.map((nav) => {
                        const isJoinUsButton = nav.name === EnglishLabels.Join_us;
                        return (
                            <Button
                                key={nav.name}
                                className={`navbar-buttons ${isJoinUsButton ? "navbar-button-join-us" : ""}`}
                                backgroundColor={(nav.name === activeSection || (isDonationPage && nav.name === EnglishLabels.Donation)) ? "#4A2C2A" : "white"}
                                color={nav.name === activeSection || (isDonationPage && nav.name === EnglishLabels.Donation) ? "white" : "#4A2C2A"}
                                onClick={() => handleNavClick(nav)}
                                fontWeight={500}
                            >
                                {isHindiLabels ? nav.hindi : nav.name}
                                {isJoinUsButton && <span className="blinking-dot" />}
                            </Button>
                        );
                    })}
            </HStack>
            {(isMobile || isTablet || isSmallScreen) ?
                <HStack w="60%" justifyContent="flex-end">
                    {renderLangSelectorMenuRenderer()}
                </HStack>
                :
                renderLangSelectorMenuRenderer()}
            {((isMobile || isTablet || isSmallScreen) && sideDrawerOpen) ?
                <SideDrawer
                    sideDrawerOpen={sideDrawerOpen}
                    onClose={() => setSideDrawerOpen(false)}
                    menu={navButtons}
                    activeSection={activeSection}
                    handleNavClick={(nav: NavMenu) => handleNavClick(nav)}
                />
                : <></>}
        </HStack>
    );
};

export default HomeNavbar;
